import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getMonthlyLeaderboardMonths, readableMonthYear } from '../helpers/dateHelpers';
import { getCardRarityClass } from '../utils/cardUtils';
import CardPreviewModal from './CardPreviewModal';

const LeaderboardList = ({ leaderboard, title, isMonthly }) => {
    const [showAllMonths, setShowAllMonths] = useState(false);
    const [previewCard, setPreviewCard] = useState(null);
    const navigate = useNavigate();

    const leaderMonths = useMemo(() => {
        return getMonthlyLeaderboardMonths();
    }, []);

    const displayedMonths = showAllMonths ? leaderMonths : leaderMonths.slice(0, 3);

    return (
        <div className="leader-container">
            <h2>{title} Leaderboard</h2>
            <div className="month-buttons">
                {isMonthly && (
                    <button className="month-btn" onClick={() => navigate('/leaderboard')}>
                        All Time
                    </button>
                )}
                {displayedMonths.map(monthYear => (
                    <button
                        key={monthYear}
                        className="month-btn"
                        onClick={() => navigate(`/leaderboard/monthly/${monthYear}`)}
                    >
                        {readableMonthYear(monthYear)}
                    </button>
                ))}
                {leaderMonths.length > 3 && (
                    <button onClick={() => setShowAllMonths(!showAllMonths)} className="toggle-months-btn">
                        {showAllMonths ? '(show less)' : '(show more)'}
                    </button>
                )}
            </div>

            {(() => {
                const hasData =
                    leaderboard.batters?.some(cat => cat.players.length > 0) ||
                    leaderboard.pitchers?.some(cat => cat.players.length > 0);

                return !hasData ? (
                    <div className="no-data-message">
                        <p>No leaderboard data available for this time period.</p>
                    </div>
                ) : (
                    <>
                    {/* Batters Section */}
                    <div>
                        <h2>Batters</h2>
                    </div>
                    <div className="leaderboard-grid">
                        {leaderboard.batters.map(statCategory => (
                    <div key={statCategory.stat} className="stat-category">
                        <h3>{statCategory.stat.toUpperCase()}</h3>
                        <ul>
                            {statCategory.players.map(player => (
                                <li key={player.cardID}>
                                    <a href={`/u/${player.User.username}`} target="_blank" rel="noopener noreferrer">{player.User.username}</a>
                                    &nbsp;
                                    <span className="lineup-card-link" onClick={() => setPreviewCard(player.Card)}>
                                        <span className={`rarity-dot ${getCardRarityClass(player.Card.rating)}`}></span>(
                                        {player.Card.rating}) {player.Card.year} {player.Card.name}
                                    </span>:{' '}
                                    {player[statCategory.stat]} (in {player.ab} AB)
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

                    {/* Pitchers Section */}
                    <div>
                        <h2>Pitchers</h2>
                    </div>
                    <div className="leaderboard-grid">
                        {leaderboard.pitchers.map(statCategory => (
                            <div key={statCategory.stat} className="stat-category">
                                <h3>{statCategory.stat.toUpperCase()}</h3>
                                <ul>
                                    {statCategory.players.map(player => (
                                        <li key={player.cardID}>
                                            <a href={`/u/${player.User.username}`} target="_blank" rel="noopener noreferrer">{player.User.username}</a>
                                            &nbsp;
                                            <span className="lineup-card-link" onClick={() => setPreviewCard(player.Card)}>
                                                <span className={`rarity-dot ${getCardRarityClass(player.Card.rating)}`}></span>(
                                                {player.Card.rating}) {player.Card.year} {player.Card.name}
                                            </span>:{' '}
                                            {player[statCategory.stat]} (in {((player.ipOuts || 0) / 3).toFixed(1)} IP)
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </>
                );
            })()}

            {previewCard && (
                <CardPreviewModal card={previewCard} onClose={() => setPreviewCard(null)} />
            )}
        </div>
    );
};

LeaderboardList.propTypes = {
    leaderboard: PropTypes.shape({
        batters: PropTypes.arrayOf(PropTypes.object),
        pitchers: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
    title: PropTypes.string.isRequired,
    isMonthly: PropTypes.bool,
};

export default LeaderboardList;
